/**
 * Mad Dad Labz Lazy Loading System
 * Optimizes loading performance for non-critical visual elements
 * Target: Improve LCP by deferring below-the-fold content
 */

class MadDadLazyLoader {
  constructor() {
    this.observer = null;
    this.imageObserver = null;
    this.animationObserver = null;
    this.init();
  }

  init() {
    try {
      // Only initialize if IntersectionObserver is supported
      if ('IntersectionObserver' in window) {
        this.setupImageLazyLoading();
        this.setupAnimationLazyLoading();
        this.setupSectionLazyLoading();
        this.setupFontLazyLoading();
      } else {
        // Fallback for older browsers - load everything immediately
        this.fallbackLoad();
      }
    } catch (error) {
      console.warn('Mad Dad Labz: Lazy loading initialization failed, falling back to immediate load', error);
      this.fallbackLoad();
    }
  }

  /**
   * Lazy load images with WebP support and fallbacks
   */
  setupImageLazyLoading() {
    const imageOptions = {
      root: null,
      rootMargin: '50px 0px', // Start loading 50px before entering viewport
      threshold: 0.01
    };

    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.imageObserver.unobserve(entry.target);
        }
      });
    }, imageOptions);

    // Observe all lazy images
    document.querySelectorAll('img[data-src], img[data-srcset]').forEach(img => {
      // Use native lazy loading when supported
      if ('loading' in HTMLImageElement.prototype) {
        img.loading = 'lazy';
      }
      
      this.imageObserver.observe(img);
    });

    // Observe background images
    document.querySelectorAll('[data-bg-src]').forEach(element => {
      this.imageObserver.observe(element);
    });
  }

  /**
   * Load individual image with WebP support
   */
  loadImage(img) {
    // Handle background images
    if (img.hasAttribute('data-bg-src')) {
      this.loadBackgroundImage(img);
      return;
    }

    // Handle regular images with WebP support
    const webpSrc = img.dataset.webpSrc;
    const fallbackSrc = img.dataset.src;
    const srcset = img.dataset.srcset;

    if (webpSrc && this.supportsWebP()) {
      img.src = webpSrc;
    } else if (fallbackSrc) {
      img.src = fallbackSrc;
    }

    if (srcset) {
      img.srcset = srcset;
    }

    // Add loading class for fade-in effect
    img.classList.add('mad-dad-loading');
    
    img.onload = () => {
      img.classList.remove('mad-dad-loading');
      img.classList.add('mad-dad-loaded');
    };

    img.onerror = () => {
      img.classList.remove('mad-dad-loading');
      img.classList.add('mad-dad-error');
      // Try fallback if WebP failed
      if (webpSrc && img.src === webpSrc && fallbackSrc) {
        img.src = fallbackSrc;
      }
    };
  }

  /**
   * Load background images with WebP support
   */
  loadBackgroundImage(element) {
    const webpSrc = element.dataset.webpBgSrc;
    const fallbackSrc = element.dataset.bgSrc;

    if (webpSrc && this.supportsWebP()) {
      element.style.backgroundImage = `url(${webpSrc})`;
    } else if (fallbackSrc) {
      element.style.backgroundImage = `url(${fallbackSrc})`;
    }

    element.classList.add('mad-dad-bg-loaded');
  }

  /**
   * Check WebP support
   */
  supportsWebP() {
    if (this.webpSupport !== undefined) {
      return this.webpSupport;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    this.webpSupport = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    return this.webpSupport;
  }

  /**
   * Lazy load animations and chaos elements
   */
  setupAnimationLazyLoading() {
    const animationOptions = {
      root: null,
      rootMargin: '100px 0px', // Start animations 100px before viewport
      threshold: 0.1
    };

    this.animationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.activateAnimations(entry.target);
          this.animationObserver.unobserve(entry.target);
        }
      });
    }, animationOptions);

    // Observe elements with lazy animations that opt-in for activation
    document.querySelectorAll('.mad-dad-lazy-animate').forEach(element => {
      if (!element.dataset.motionScope || element.dataset.motionScope === 'hero') {
        this.animationObserver.observe(element);
      }
    });

    // Observe chaos grid elements that explicitly opt-in
    document.querySelectorAll('.mad-dad-stagger-reveal').forEach(element => {
      if (!element.dataset.motionScope || element.dataset.motionScope === 'hero') {
        this.animationObserver.observe(element);
      }
    });

    // Observe laboratory icons inside hero contexts only
    document.querySelectorAll('.mad-dad-hero .mad-dad-laboratory-icon').forEach(element => {
      this.animationObserver.observe(element);
    });
  }

  /**
   * Activate animations when elements enter viewport
   */
  activateAnimations(element) {
    // Respect reduced motion preferences
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.classList.add('mad-dad-no-motion');
      return;
    }

    const scope = element.dataset.motionScope || 'hero';

    if (scope !== 'hero' && scope !== 'opt-in') {
      return;
    }

    // Activate stagger reveal animations
    if (element.classList.contains('mad-dad-stagger-reveal')) {
      element.classList.add('revealed');
    }

    // Activate laboratory icon animations
    if (element.classList.contains('mad-dad-laboratory-icon') && element.closest('.mad-dad-hero')) {
      element.classList.add('animate');
    }

    // Activate general lazy animations
    if (element.classList.contains('mad-dad-lazy-animate')) {
      element.classList.add('animate-active');
    }
  }

  /**
   * Lazy load non-critical sections
   */
  setupSectionLazyLoading() {
    const sectionOptions = {
      root: null,
      rootMargin: '200px 0px', // Load sections 200px before viewport
      threshold: 0.01
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadSection(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, sectionOptions);

    // Observe lazy sections
    document.querySelectorAll('.mad-dad-lazy-section').forEach(section => {
      this.observer.observe(section);
    });
  }

  /**
   * Load section content and styles
   */
  loadSection(section) {
    // Load section-specific CSS if specified
    const cssHref = section.dataset.lazyCss;
    if (cssHref) {
      this.loadCSS(cssHref);
    }

    // Load section-specific JS if specified
    const jsHref = section.dataset.lazyJs;
    if (jsHref) {
      this.loadJS(jsHref);
    }

    // Activate section content
    section.classList.add('mad-dad-section-loaded');

    // Load any images within the section
    section.querySelectorAll('img[data-src]').forEach(img => {
      this.loadImage(img);
    });
  }

  /**
   * Lazy load non-critical fonts
   */
  setupFontLazyLoading() {
    // Load additional font weights after critical content
    setTimeout(() => {
      this.loadFont('Baloo 2', '500,600,700');
      this.loadFont('Inter', '300,500,600,700');
    }, 1000);
  }

  /**
   * Load font with font-display: swap
   */
  loadFont(family, weights) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${family.replace(' ', '+')}:wght@${weights}&display=swap`;
    document.head.appendChild(link);
  }

  /**
   * Dynamically load CSS
   */
  loadCSS(href) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = 'print';
    link.onload = () => {
      link.media = 'all';
    };
    document.head.appendChild(link);
  }

  /**
   * Dynamically load JavaScript
   */
  loadJS(src) {
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  }

  /**
   * Fallback for browsers without IntersectionObserver
   */
  fallbackLoad() {
    // Load all images immediately
    document.querySelectorAll('img[data-src]').forEach(img => {
      this.loadImage(img);
    });

    // Load all background images
    document.querySelectorAll('[data-bg-src]').forEach(element => {
      this.loadBackgroundImage(element);
    });

    // Activate all animations
    document.querySelectorAll('.mad-dad-lazy-animate, .mad-dad-stagger-reveal').forEach(element => {
      this.activateAnimations(element);
    });

    // Load all sections
    document.querySelectorAll('.mad-dad-lazy-section').forEach(section => {
      this.loadSection(section);
    });
  }

  /**
   * Preload critical resources for next page
   */
  preloadNextPage() {
    // Preload likely next page resources
    const productLinks = document.querySelectorAll('a[href*="/products/"]');
    const collectionLinks = document.querySelectorAll('a[href*="/collections/"]');
    
    // Preload first few product/collection pages on hover
    [...productLinks, ...collectionLinks].slice(0, 3).forEach(link => {
      link.addEventListener('mouseenter', () => {
        this.preloadPage(link.href);
      }, { once: true });
    });
  }

  /**
   * Preload page resources
   */
  preloadPage(url) {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }
}

// CSS for lazy loading effects
const lazyLoadCSS = `
  .mad-dad-loading {
    opacity: 0.3;
    transition: opacity 0.3s ease;
  }
  
  .mad-dad-loaded {
    opacity: 1;
  }
  
  .mad-dad-error {
    opacity: 0.5;
    background-color: rgba(var(--color-foreground), 0.1);
  }
  
  .mad-dad-bg-loaded {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
  
  .mad-dad-lazy-section {
    min-height: 200px;
    background-color: rgba(var(--color-foreground), 0.02);
  }
  
  .mad-dad-section-loaded {
    background-color: transparent;
  }
  
  .mad-dad-stagger-reveal {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  
  .mad-dad-stagger-reveal.revealed {
    opacity: 1;
    transform: translateY(0);
  }
  
  .mad-dad-lazy-animate {
    opacity: 0;
    transform: scale(0.95);
    transition: opacity 0.4s ease, transform 0.4s ease;
  }
  
  .mad-dad-lazy-animate.animate-active {
    opacity: 1;
    transform: scale(1);
  }
  
  /* Respect reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .mad-dad-stagger-reveal,
    .mad-dad-lazy-animate,
    .mad-dad-loading,
    .mad-dad-loaded {
      transition: none !important;
      animation: none !important;
    }
    
    .mad-dad-no-motion {
      opacity: 1 !important;
      transform: none !important;
    }
  }
`;

// Inject lazy loading CSS
const style = document.createElement('style');
style.textContent = lazyLoadCSS;
document.head.appendChild(style);

// Initialize lazy loader when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.madDadLazyLoader = new MadDadLazyLoader();
  });
} else {
  window.madDadLazyLoader = new MadDadLazyLoader();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MadDadLazyLoader;
}